import { ActionIcon, Loader } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "@/services";
import {
  type StaticTextTranslator,
  translateIdentity,
} from "@/utils/staticTextTranslator";
import styles from "./VoicePlaybackBubble.module.css";

interface VoicePlaybackBubbleProps {
  audioKey: string;
  sessionId: string | null;
  translate?: StaticTextTranslator;
}

// Refresh the presigned URL slightly before it actually expires so we never
// hand the audio element a URL S3 will reject mid-load.
const PLAYBACK_URL_EXPIRY_BUFFER_MS = 5_000;

/**
 * AS-1816 — legacy-style play/pause button rendered in place of an outgoing
 * voice message bubble. Matches the production screener: just the control, no
 * surrounding "Voice response submitted" label or text bubble.
 *
 * Click → fetch presigned URL → play. Subsequent clicks toggle play/pause
 * on the cached audio element until the presigned URL nears expiry, at
 * which point the next play re-fetches a fresh URL.
 */
export const VoicePlaybackBubble = ({
  audioKey,
  sessionId,
  translate = translateIdentity,
}: VoicePlaybackBubbleProps) => {
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Tracks whether the component is still mounted so the async play path can
  // bail out if `getVoiceResponsePlaybackUrl` resolves after teardown — without
  // this guard a late resolution would create a new Audio and play it in the
  // background after the user already navigated away.
  const isMountedRef = useRef(true);

  // Stop and detach audio on unmount so a teardown mid-playback doesn't keep
  // the recording playing in the background.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const isPlaybackUrlStale = useCallback(
    () => !expiresAt || Date.now() >= expiresAt - PLAYBACK_URL_EXPIRY_BUFFER_MS,
    [expiresAt],
  );

  const handlePlay = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    if (audioRef.current && !isPlaybackUrlStale()) {
      try {
        await audioRef.current.play();
      } catch {
        // Falls through to the refresh branch on the user's next click.
        setExpiresAt(null);
      }
      return;
    }

    setIsLoading(true);

    try {
      const { playbackUrl: url, expiresAt: responseExpiresAt } =
        await apiService.getVoiceResponsePlaybackUrl(sessionId, audioKey);
      if (!isMountedRef.current) {
        return;
      }
      setExpiresAt(Date.parse(responseExpiresAt));
      audioRef.current?.pause();
      const audio = new Audio(url);
      audio.addEventListener("ended", () => setIsPlaying(false));
      audio.addEventListener("pause", () => setIsPlaying(false));
      audio.addEventListener("play", () => setIsPlaying(true));
      audio.addEventListener("error", () => {
        // Mark the URL as stale so the next click re-fetches a fresh one
        // instead of replaying the dead audio element.
        setExpiresAt(null);
        setIsPlaying(false);
      });
      audioRef.current = audio;
      await audio.play();
    } catch {
      if (!isMountedRef.current) {
        return;
      }
      // Leave expiresAt null so the next click retries instead of locking the
      // user out of playback for the rest of the session.
      setExpiresAt(null);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [audioKey, isPlaybackUrlStale, sessionId]);

  const handlePause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const onClick = isPlaying ? handlePause : handlePlay;

  return (
    <ActionIcon
      className={styles.playButton}
      onClick={onClick}
      disabled={isLoading || !sessionId}
      aria-label={
        isPlaying
          ? translate("Pause voice response")
          : translate("Play voice response")
      }
      data-testid="voice-playback-bubble__play"
    >
      {isLoading ? (
        <Loader size={18} color="white" />
      ) : (
        <span className={styles.iconCircle} aria-hidden="true">
          {isPlaying ? (
            <span className={styles.pauseIcon} />
          ) : (
            <span className={styles.playIcon} />
          )}
        </span>
      )}
    </ActionIcon>
  );
};
